/**
 * useMeals — TanStack Query hook over GET /api/meals (Wave 3 — meal planner).
 *
 * Meals are created only by confirming a meal draft in the assistant; this hook
 * is read-only. Keyed by the [from, to] window so the /meny week navigation
 * caches each week independently. The confirm mutation invalidates ['meals'].
 */
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type { CreateMealDraftInput, Meal } from '@stell/shared';
import { ApiError } from '../admin/useMembers';

export interface MealsResponse {
  meals: Meal[];
  from: string;
  to: string;
}

export function useMeals(from: string, to: string) {
  return useQuery<MealsResponse>({
    queryKey: ['meals', from, to],
    queryFn: async () => {
      const r = await fetch(`/api/meals?from=${from}&to=${to}`, { credentials: 'same-origin' });
      if (!r.ok) throw new Error(`meals_fetch_failed: ${r.status}`);
      return (await r.json()) as MealsResponse;
    },
    staleTime: 5 * 60_000,
  });
}

async function toApiError(res: Response): Promise<ApiError> {
  let code = 'unknown';
  try {
    const body = (await res.json()) as { error?: unknown };
    if (typeof body.error === 'string') code = body.error;
  } catch {
    /* non-JSON body */
  }
  return new ApiError(res.status, code);
}

export type MealPayload = CreateMealDraftInput;

/**
 * PATCH /api/meals/:id — edit a planned meal. Admin-scope-gated (the server
 * forces fresh re-auth), so a 403 throws ApiError for the caller's runGuarded
 * to open <AdminReauthModal>; a 422 allergen_blocked surfaces the HARD-BLOCK so
 * the caller can re-submit with `override`.
 */
export function useUpdateMeal() {
  const qc = useQueryClient();
  return useMutation<Meal, ApiError, { id: string; payload: MealPayload; override?: boolean }>({
    mutationFn: async ({ id, payload, override }) => {
      const res = await fetch(`/api/meals/${id}`, {
        method: 'PATCH',
        credentials: 'same-origin',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(override ? { payload, override: true } : { payload }),
      });
      if (!res.ok) throw await toApiError(res);
      const body = (await res.json()) as { meal: Meal };
      return body.meal;
    },
    onSuccess: () => void qc.invalidateQueries({ queryKey: ['meals'] }),
  });
}

/** DELETE /api/meals/:id — remove a planned meal (admin-scope-gated). */
export function useDeleteMeal() {
  const qc = useQueryClient();
  return useMutation<void, ApiError, string>({
    mutationFn: async (id: string) => {
      const res = await fetch(`/api/meals/${id}`, { method: 'DELETE', credentials: 'same-origin' });
      if (!res.ok) throw await toApiError(res);
    },
    onSuccess: () => void qc.invalidateQueries({ queryKey: ['meals'] }),
  });
}
